import { startTransition, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth';
import {
  filterNavigationTree,
  permissionSnapshotFromAccessToken,
  useTenantEntitlements,
} from '../../entitlements';
import {
  findAncestorGroupIds,
  leafMatchesPath,
  navigationTree,
  type NavGroup,
  type NavLeaf,
  type NavNode,
} from '../../navigation';
import { testIds } from '../../testing/testIds';
import { mergeExpandedGroupIds } from './mergeExpandedGroupIds';

function topLevelPadClass(depth: number): string {
  return depth === 0 ? 'pl-2' : '';
}

function NavSidebarIcon({ file }: { file: string }) {
  return (
    <img
      src={`/icons/sidebar/${file}`}
      alt=""
      width={24}
      height={24}
      className="shrink-0 object-contain select-none pointer-events-none"
      draggable={false}
    />
  );
}

function NavRowIconSlot({ iconFile }: { iconFile?: string }) {
  return (
    <span className="inline-flex w-6 shrink-0 justify-center items-center" aria-hidden>
      {iconFile ? <NavSidebarIcon file={iconFile} /> : <span className="w-6" />}
    </span>
  );
}

function NavLeafLink({
  leaf,
  pathname,
  search,
  onNavigate,
}: {
  leaf: NavLeaf;
  pathname: string;
  search: string;
  onNavigate?: () => void;
}) {
  const active = leafMatchesPath(pathname, leaf, search);
  return (
    <NavLink
      to={leaf.to}
      end
      data-testid={testIds.shell.navLeaf(leaf.id)}
      // Highlight comes from `leafMatchesPath` (search-aware), not NavLink prefix matching.
      // `end` stops `/clients` from stealing aria-current on `/clients/onboard` and the BO routes.
      aria-current={active ? 'page' : undefined}
      onClick={onNavigate}
      className={() =>
        active
          ? 'flex items-center gap-2 w-full text-left py-1.5 pr-1 rounded-lg transition-colors bg-primary-100 text-primary-800 font-medium'
          : 'flex items-center gap-2 w-full text-left py-1.5 pr-1 rounded-lg transition-colors text-gray-600 hover:bg-gray-50 hover:text-gray-900'
      }
    >
      <NavRowIconSlot iconFile={leaf.iconFile} />
      <span className="min-w-0 text-sm leading-snug">{leaf.label}</span>
    </NavLink>
  );
}

function NavGroupBranch({
  group,
  pathname,
  search,
  depth,
  expandedIds,
  toggle,
  onNavigate,
}: {
  group: NavGroup;
  pathname: string;
  search: string;
  depth: number;
  expandedIds: Set<string>;
  toggle: (id: string) => void;
  onNavigate?: () => void;
}) {
  const isExpanded = expandedIds.has(group.id);
  const hasActiveDescendant = branchHasActiveLeaf(group.children, pathname, search);
  const outerPad = topLevelPadClass(depth);

  return (
    <div className={`mb-0.5 ${outerPad}`}>
      <button
        type="button"
        data-testid={testIds.shell.navGroup(group.id)}
        // Exposes expand/collapse state to tests and assistive tech, so neither has to
        // read the chevron's rotation class to know whether children are visible.
        aria-expanded={isExpanded}
        onClick={() => toggle(group.id)}
        className={`flex items-center gap-2 w-full text-left py-1.5 pr-1 rounded-lg transition-colors ${
          hasActiveDescendant && depth === 0
            ? 'bg-primary-50/80 text-primary-800 ring-1 ring-primary-200/60'
            : hasActiveDescendant
              ? 'text-gray-900'
              : 'text-gray-700 hover:bg-gray-50'
        }`}
      >
        <NavRowIconSlot iconFile={group.iconFile} />
        <span className="min-w-0 flex-1 text-sm font-medium leading-snug">{group.label}</span>
        <svg
          className={`w-4 h-4 shrink-0 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="mt-0.5 ml-1 border-l border-gray-200 pl-2 space-y-0.5">
          {group.children.map((child) => (
            <NavTree
              key={child.id}
              node={child}
              pathname={pathname}
              search={search}
              depth={depth + 1}
              expandedIds={expandedIds}
              toggle={toggle}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function branchHasActiveLeaf(nodes: NavNode[], pathname: string, search: string): boolean {
  for (const node of nodes) {
    if (node.kind === 'leaf') {
      if (leafMatchesPath(pathname, node, search)) return true;
    } else if (node.kind === 'group') {
      if (branchHasActiveLeaf(node.children, pathname, search)) return true;
    }
  }
  return false;
}

function NavTree({
  node,
  pathname,
  search,
  depth,
  expandedIds,
  toggle,
  onNavigate,
}: {
  node: NavNode;
  pathname: string;
  search: string;
  depth: number;
  expandedIds: Set<string>;
  toggle: (id: string) => void;
  onNavigate?: () => void;
}) {
  if (node.kind === 'leaf') {
    return <NavLeafLink leaf={node} pathname={pathname} search={search} onNavigate={onNavigate} />;
  }
  if (node.kind === 'placeholder') {
    return (
      <div
        data-testid={testIds.shell.navPlaceholder(node.id)}
        className={`flex items-center gap-2 py-1.5 pr-1 text-sm text-gray-400 italic cursor-not-allowed select-none ${topLevelPadClass(depth)}`}
        title="Coming soon"
      >
        <NavRowIconSlot iconFile={node.iconFile} />
        <span className="min-w-0 leading-snug">{node.label}</span>
      </div>
    );
  }
  return (
    <NavGroupBranch
      group={node}
      pathname={pathname}
      search={search}
      depth={depth}
      expandedIds={expandedIds}
      toggle={toggle}
      onNavigate={onNavigate}
    />
  );
}

interface SidebarProps {
  pinned: boolean;
  open: boolean;
  canPin: boolean;
  onTogglePin: () => void;
  onClose: () => void;
  onNavigate?: () => void;
}

export function Sidebar({ pinned, open, canPin, onTogglePin, onClose, onNavigate }: SidebarProps) {
  const sidebarRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const pathname = location.pathname;
  const search = location.search;
  const { user, getAccessToken } = useAuth();
  const { features, isResolved } = useTenantEntitlements();
  const snapshot = useMemo(
    () => permissionSnapshotFromAccessToken(getAccessToken() ?? user?.access_token ?? null),
    [getAccessToken, user]
  );
  const visibleTree = useMemo(
    () => (isResolved ? filterNavigationTree(navigationTree, features, snapshot) : []),
    [features, isResolved, snapshot]
  );

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    const chain = findAncestorGroupIds(pathname, navigationTree, [], search);
    chain?.forEach((id) => initial.add(id));
    if (initial.size === 0) {
      // WPF starts Clients first, but LinkPoint and other narrow plans prune it.
      // Portfolio Management is the first group that stays on Standard / LinkPoint.
      initial.add('portfolio-management');
    }
    return initial;
  });

  useEffect(() => {
    const chain = findAncestorGroupIds(pathname, visibleTree, [], search);
    if (!chain?.length) return;
    startTransition(() => {
      setExpandedIds((prev) => mergeExpandedGroupIds(prev, chain));
    });
  }, [pathname, search, visibleTree]);

  useEffect(() => {
    // React 18's DOM types predate `inert`; set it directly so an off-canvas
    // closed drawer cannot receive keyboard focus while it remains mounted for its transition.
    sidebarRef.current?.toggleAttribute('inert', !pinned && !open);
  }, [open, pinned]);

  useEffect(() => {
    if (pinned || !open) return;
    sidebarRef.current?.querySelector<HTMLButtonElement>('[data-navigation-initial-focus="true"]')?.focus();
  }, [open, pinned]);

  const toggle = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <aside
      ref={sidebarRef}
      id="main-navigation"
      className={
        pinned
          ? 'w-64 shrink-0 bg-white border-r border-gray-200 flex flex-col text-left'
          : `fixed inset-y-0 left-0 z-50 flex w-72 max-w-[calc(100vw-2rem)] flex-col border-r border-gray-200 bg-white text-left shadow-xl transition-transform duration-200 ease-out ${
              open ? 'translate-x-0' : '-translate-x-full'
            }`
      }
      data-testid={testIds.shell.sidebar}
      aria-hidden={pinned || open ? undefined : true}
    >
      <div className="flex items-start justify-between gap-2 border-b border-gray-200 px-3 py-3 shrink-0">
        <div>
          <h1 className="text-xl font-bold text-primary-600 leading-tight">LedgerVue</h1>
          <p className="text-xs text-gray-500 mt-0.5">Portfolio Management</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {canPin ? (
            <button
              type="button"
              onClick={onTogglePin}
              data-testid={testIds.shell.navigationPinButton}
              aria-label={pinned ? 'Unpin navigation' : 'Pin navigation'}
              aria-pressed={pinned}
              title={pinned ? 'Unpin navigation' : 'Pin navigation'}
              className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-5 w-5" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" d="m9 4 6 6m-3-3 4 4-3 3v5l-2-2-2 2v-5l-3-3 3-3 4 4" />
              </svg>
            </button>
          ) : null}
          {!pinned ? (
            <button
              type="button"
              onClick={onClose}
              data-navigation-initial-focus="true"
              aria-label="Close navigation"
              className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-5 w-5" aria-hidden>
                <path strokeLinecap="round" d="m6 6 12 12M18 6 6 18" />
              </svg>
            </button>
          ) : null}
        </div>
      </div>

      <nav className="flex-1 py-2 overflow-y-auto overflow-x-hidden text-left" aria-label="Main">
        {visibleTree.map((node) => (
          <NavTree
            key={node.id}
            node={node}
            pathname={pathname}
            search={search}
            depth={0}
            expandedIds={expandedIds}
            toggle={toggle}
            onNavigate={onNavigate}
          />
        ))}
      </nav>

      <div className="px-3 py-3 border-t border-gray-200 text-xs text-gray-400 shrink-0">
        LedgerVue v0.1.0
      </div>
    </aside>
  );
}
